/**
 * Zoom Contact Center config resolution.
 *
 * Default configuration is selected at build time via `VITE_ZOOM_REGION`
 * (see `vite.config.mts`):
 *   dev  → 'us01'   (local vite dev + CI dev environment)
 *   test → 'eu01'   (CI test environment)
 *   prod → 'eu01'   (CI prod environment)
 *
 * In NON-prod builds, the active (region, entryId) pair can be overridden:
 *   1. Query string — `?region=us01&entryId=ENTRY_ID` (shareable test links;
 *      applies for this page load only, does not write localStorage)
 *   2. DevTools — `window.zoom.set(...)` via {@link installZoomOverrideHelper},
 *      persisted in localStorage so it survives the reload required to
 *      re-load the Zoom SDK script
 * Query string wins when both a valid QS pair and a localStorage override
 * are present.
 *
 * The override capability is INTENTIONALLY stripped from prod bundles by
 * `import.meta.env.VITE_ZOOM_OVERRIDE_ENABLED` being substituted at build
 * time with the literal `false`, which esbuild then dead-code eliminates.
 * Verifiable post-build by `grep`-ing the prod bundle for `zoom-override`,
 * `zoom.set`, `installZoomOverrideHelper`, etc. — none should appear.
 */

import { clearJoinTrace, readJoinTrace } from './joinTrace';

const ZOOM_CONFIG = {
  us01: {
    sdkUrl: 'https://us01ccistatic.zoom.us/us01cci/web-sdk/video-client.js',
    entryId: 'HPUEN4-kSrGg_Pj78S2Ouw',
  },
  eu01: {
    sdkUrl: 'https://eu01ccistatic.zoom.us/eu01cci/web-sdk/video-client.js',
    entryId: 'pBPjbrJeTk6OAxL_Bm-iHw',
  },
} as const;

type ZoomRegion = keyof typeof ZOOM_CONFIG;

const ACTIVE_REGION: ZoomRegion =
  (import.meta.env.VITE_ZOOM_REGION as ZoomRegion) in ZOOM_CONFIG
    ? (import.meta.env.VITE_ZOOM_REGION as ZoomRegion)
    : 'eu01';

/**
 * The default config for the current build environment. Exported so tests can
 * derive their expected values from the same inlined constant rather than
 * hardcoding a region that differs between dev ('us01') and test/prod ('eu01').
 */
export const DEFAULT_ZOOM_CONFIG = {
  region: ACTIVE_REGION,
  sdkUrl: ZOOM_CONFIG[ACTIVE_REGION].sdkUrl,
  entryId: ZOOM_CONFIG[ACTIVE_REGION].entryId,
} as const;

export interface ResolvedZoomConfig {
  sdkUrl: string;
  entryId: string;
  region: string;
  overrideActive: boolean;
}

const STORAGE_KEY = 'zoom-override';

interface StoredOverride {
  region: string;
  entryId: string;
}

/**
 * Region must be lowercase alphanumeric and short — it is interpolated into
 * the Zoom SDK URL template, so anything that could escape the URL template
 * (slashes, dots, query separators, percent-encodings) is rejected. The
 * resulting URL still has to pass the CSP `script-src https://*.zoom.us`
 * check at runtime, which is a second line of defence.
 */
function isValidRegion(value: unknown): value is string {
  return (
    typeof value === 'string' && value.length > 0 && value.length <= 16 && /^[a-z0-9]+$/.test(value)
  );
}

/**
 * Zoom-style entry IDs are opaque tokens — bound the length and the
 * character set so a malformed override can't propagate weird strings into
 * the SDK init call.
 */
function isValidEntryId(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length > 0 &&
    value.length <= 128 &&
    /^[A-Za-z0-9_-]+$/.test(value)
  );
}

function buildSdkUrl(region: string): string {
  return `https://${region}ccistatic.zoom.us/${region}cci/web-sdk/video-client.js`;
}

function readOverride(): StoredOverride | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredOverride>;
    if (!isValidRegion(parsed.region) || !isValidEntryId(parsed.entryId)) {
      return null;
    }
    return { region: parsed.region, entryId: parsed.entryId };
  } catch {
    return null;
  }
}

/**
 * Parse a (region, entryId) override from a URL search string. Both params
 * must be present and valid; otherwise returns null (same as a malformed
 * localStorage override). Does not write localStorage.
 */
function readOverrideFromSearch(search: string): StoredOverride | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const region = params.get('region');
  const entryId = params.get('entryId');
  if (!isValidRegion(region) || !isValidEntryId(entryId)) {
    return null;
  }
  return { region, entryId };
}

/** Join states that `?previewJoin=` can hold the page in without calling Zoom. */
export type JoinPreview = 'connecting' | 'error';

/**
 * Read `?previewJoin=connecting|error`, which makes the "Start appointment"
 * button exercise the join UI without starting a call. Zoom's own widget
 * covers the page within a second of a real join, so the connecting copy —
 * including the message that replaces it after 15 seconds — is otherwise only
 * visible when Zoom is genuinely slow. Non-prod only; reload to leave the
 * preview.
 */
export function readJoinPreview(search?: string): JoinPreview | null {
  if (!import.meta.env.VITE_ZOOM_OVERRIDE_ENABLED) return null;

  const raw =
    search ?? (typeof window !== 'undefined' && window.location ? window.location.search : '');
  const value = new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw).get('previewJoin');
  return value === 'connecting' || value === 'error' ? value : null;
}

/**
 * Resolve the Zoom SDK URL + entryId to use for this page load. Called at
 * the two consumer sites in `AppointmentForm` (script tag injection +
 * `VideoClient.init`) and by the non-prod override banner. In non-prod
 * builds, prefers a valid query-string override, then localStorage; in prod
 * the entire override branch is dead-code-eliminated by the top-level
 * `import.meta.env.VITE_ZOOM_OVERRIDE_ENABLED` guard.
 */
export function resolveZoomConfig(): ResolvedZoomConfig {
  if (import.meta.env.VITE_ZOOM_OVERRIDE_ENABLED) {
    const search = typeof window !== 'undefined' && window.location ? window.location.search : '';
    const override = readOverrideFromSearch(search) ?? readOverride();
    if (override) {
      return {
        sdkUrl: buildSdkUrl(override.region),
        entryId: override.entryId,
        region: override.region,
        overrideActive: true,
      };
    }
  }
  return {
    sdkUrl: ZOOM_CONFIG[ACTIVE_REGION].sdkUrl,
    entryId: ZOOM_CONFIG[ACTIVE_REGION].entryId,
    region: ACTIVE_REGION,
    overrideActive: false,
  };
}

/**
 * Install the `window.zoom` DevTools helper for non-prod environments.
 *
 * Usage from the browser console:
 *   zoom.set("us01", "ENTRY_ID")  — save a (region, entryId) override
 *   zoom.get()                     — read the current override
 *   zoom.clear()                   — remove the override
 *   zoom.help()                    — print usage
 *   zoom.trace()                   — print the join lifecycle trace
 *   zoom.clearTrace()              — empty the join trace
 *
 * Shareable test links (non-prod only):
 *   ?region=us01&entryId=ENTRY_ID  — applies for this page load; does not
 *                                    write localStorage; wins over zoom.set
 *
 * Reload the page after `set`/`clear` for changes to take effect — the Zoom
 * SDK `<script>` tag is created once at app startup, so the new URL has to
 * be picked up on the next page load.
 */
export function installZoomOverrideHelper(): void {
  if (!import.meta.env.VITE_ZOOM_OVERRIDE_ENABLED) return;
  if (typeof window === 'undefined') return;

  const helper = {
    set(region: unknown, entryId: unknown): string {
      if (!isValidRegion(region)) {
        const msg = '[zoom] invalid region. Use lowercase alphanumeric (e.g. "us01", "eu01").';
        console.error(msg);
        return msg;
      }
      if (!isValidEntryId(entryId)) {
        const msg = '[zoom] invalid entryId. Must be 1-128 chars of [A-Za-z0-9_-].';
        console.error(msg);
        return msg;
      }
      const next: StoredOverride = { region, entryId };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch (err) {
        const msg = '[zoom] failed to write to localStorage';
        console.error(msg, err);
        return msg;
      }
      const msg = `[zoom] override saved (region=${region}). Reload the page to apply.`;
      console.info(msg, next);
      return msg;
    },
    get(): StoredOverride | null {
      const cur = readOverride();
      console.info('[zoom] current override:', cur);
      return cur;
    },
    clear(): string {
      localStorage.removeItem(STORAGE_KEY);
      const msg = '[zoom] override cleared. Reload the page to revert to defaults.';
      console.info(msg);
      return msg;
    },
    trace(): string[] {
      const lines = readJoinTrace();
      console.info(
        lines.length ? `[zoom] join trace:\n${lines.join('\n')}` : '[zoom] join trace is empty.'
      );
      return lines;
    },
    clearTrace(): string {
      clearJoinTrace();
      const msg = '[zoom] join trace cleared.';
      console.info(msg);
      return msg;
    },
    help(): void {
      console.info(
        [
          '[zoom] Override (non-prod only):',
          '  Query string (shareable link, this page load only):',
          '    ?region=us01&entryId=ENTRY_ID',
          '  DevTools (persisted in localStorage until cleared):',
          '    zoom.set("us01", "ENTRY_ID")  — set a region + entryId override',
          '    zoom.get()                     — read the localStorage override',
          '    zoom.clear()                   — remove the localStorage override',
          '  Query string wins over localStorage when both are present.',
          '  Join trace (survives reloads, kept in sessionStorage):',
          '    zoom.trace()                   — print the join lifecycle trace',
          '    zoom.clearTrace()              — empty the trace',
          'Region must be lowercase alphanumeric (e.g. "us01", "eu01").',
          'EntryId must be 1-128 chars of [A-Za-z0-9_-].',
          'Reload the page after set/clear to apply.',
        ].join('\n')
      );
    },
  };

  (window as unknown as { zoom: typeof helper }).zoom = Object.freeze(helper);

  const cfg = resolveZoomConfig();
  if (cfg.overrideActive) {
    console.warn(
      '[zoom] active override detected:',
      { region: cfg.region, entryId: cfg.entryId },
      '— run zoom.clear() to remove a localStorage override (query-string overrides clear when the params are removed).'
    );
  } else {
    console.info('[zoom] override available in this environment. Run zoom.help() for usage.');
  }
}
